import { type FC, useMemo } from 'react';
import styled from 'styled-components';
import { NamedContentEntryText } from '../ContentEntryText/NamedContentEntryText';
import { Icon, IconType } from '../Props/Icon/Icon';
import { Modal } from '../Modal';
import { NudgeType } from './enums/NudgeType';
import { nudgeModalDetailsMap } from './utils/nudgeModalDetailsMap';
import type { BehaviorType } from '../TeamBehaviors/enum/BehaviorType';
import SendNudgeIcon from './assets/SendNudge.svg';
import { ModalSize } from '../Modal/Modal';
import type { ScoreBreakdownType } from '../../lib/types/TeamBehavior';
import { Spinner } from '../Spinner/Spinner';
import { useNudgeTemplatesQuery } from '../../apiHooks/useNudgeTemplatesQuery';
import { AdaptiveCardComponent } from './components/CustomAdaptiveCard';

export type NudgeModalConfigs = {
  isOpen: boolean;
  type?: NudgeType;
  behaviorType?: BehaviorType;
  behavior?: ScoreBreakdownType;
  userId?: string;
};

type NudgeModalModalProps = {
  nudgeConfigs: NudgeModalConfigs;
  onClose: () => void;
  onSendNudge: () => void;
  isSending?: boolean;
};

export const NudgeModal: FC<NudgeModalModalProps> = ({
  nudgeConfigs,
  onSendNudge,
  onClose,
  isSending,
}: NudgeModalModalProps) => {
  const modalTypeDetails = nudgeModalDetailsMap[nudgeConfigs.type || NudgeType.CONGRATS];
  const { data: nudgeTemplateData } = useNudgeTemplatesQuery({ nudgeConfigs });
  const templateAsAdaptiveCard = useMemo(
    () => (nudgeTemplateData && nudgeTemplateData.payload ? JSON.parse(nudgeTemplateData.payload) : {}),
    [nudgeTemplateData],
  );

  return (
    <Modal isOpen={nudgeConfigs.isOpen} onClose={onClose} hideCloseButton size={ModalSize.MEDIUM}>
      <ModalHeader>
        <Title>
          {modalTypeDetails.title}{' '}
          <GeneralNotification>
            <NamedContentEntryText container="span" refKey="managerTrainingNudgeDialog" subKey="notification" />
          </GeneralNotification>
        </Title>
        <CloseButton onClick={onClose}>
          <Icon icon={IconType.TIMES} width={16} height={16} />
        </CloseButton>
      </ModalHeader>
      <ModalContent>
        <CardOuter>
          <Card>
            <AdaptiveCardComponent cardData={templateAsAdaptiveCard}></AdaptiveCardComponent>
          </Card>
        </CardOuter>
      </ModalContent>
      <ModalBottom>
        <InfoLeanguage>
          <Icon fill={'none'} icon={IconType.INFO_TOOLTIP} width={16} height={16} />
          <NamedContentEntryText container="span" refKey="managerTrainingNudgeDialog" subKey="translationTooltip" />
        </InfoLeanguage>
        <SendNudgeButton onClick={onSendNudge}>
          {isSending ? (
            <Spinner size="15px" color="#fff" />
          ) : (
            <>
              <SendNudgeIcon />
              <NamedContentEntryText container="span" refKey="managerTrainingNudgeDialog" subKey="sendNudge" />
            </>
          )}
        </SendNudgeButton>
      </ModalBottom>
    </Modal>
  );
};

const ModalContent = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
  padding: 24px 16px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0), rgba(0, 0, 0, 0.02));
`;

const ModalHeader = styled.div`
  width: 100%;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${({ theme }) => theme.colors.pageBackground};
  border-bottom: 1px solid #dfe1e6;
`;

const InfoLeanguage = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
`;

const GeneralNotification = styled.div`
  padding: 2px 4px;
  background-color: #fff9eb;
  color: #762a11;
  font-size: 12px;
  border-radius: 6px;
`;

const Title = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  font-size: ${({ theme }) => theme.fontSizes.heading};
  color: ${({ theme }) => theme.colors.heading};
  font-weight: 600;
  user-select: none;
`;

const CloseButton = styled.div`
  cursor: pointer;
`;

const ModalBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  box-shadow: ${({ theme }) => theme.shadow.light};
`;

const SendNudgeButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 30px;
  min-width: 112px;
  padding: 6px 8px;
  gap: 8px;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.pageBackground};
  border-radius: 8px;
  cursor: pointer;
`;

const Card = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 12px;
  background-color: #fff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  overflow: visible;
`;

const CardOuter = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px 50px;
  margin: 0 24px;
  border-radius: 8px;
  background-color: #d5daee;
  overflow: hidden;
  scrollbar-color: #b6bedc transparent;
`;
